import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef, SimpleSnackBar } from '@angular/material';
import { environment } from '../../environments/environment';

/**
 * Detecta cuando el backend sirve una version mas reciente que la cargada en
 * el browser. Trabaja sin polling: el `AppVersionInterceptor` lee el header
 * `X-App-Version` de cada respuesta HTTP y se lo pasa a este servicio.
 *
 * Logica:
 *   - `localVersion` = environment.appVersion (versión del bundle cargado).
 *   - Cada respuesta del backend reporta serverVersion y se compara con local.
 *   - Si difieren -> abre un snackbar persistente (una sola vez mientras dure
 *     el mismatch).
 *   - Si vuelven a coincidir (admin revierte la version en BD), CIERRA el
 *     snackbar y resetea el flag para poder notificar de nuevo a futuro.
 *   - Al hacer click en "Actualizar" -> location.reload(true) descarta cache.
 */
@Injectable({ providedIn: 'root' })
export class AppVersionService {

  private readonly localVersion: string = environment.appVersion || '';
  // Periodo de gracia tras click en "Actualizar". El cache del backend tiene
  // TTL ~30s, asi que al recargar la app el primer header puede seguir trayendo
  // la version vieja durante un momento. Ignoramos mismatches en esta ventana.
  private readonly GRACE_AFTER_RELOAD_MS = 60_000;
  private graceUntil = 0;
  private snackRef: MatSnackBarRef<SimpleSnackBar> | null = null;

  constructor(private snackBar: MatSnackBar) {
    // Si la sesion previa hizo click en "Actualizar", entramos en periodo de
    // gracia para no abrir el toast de inmediato con la version vieja del cache.
    try {
      const ts = parseInt(sessionStorage.getItem('appVersionReloadAt') || '0', 10);
      if (ts && (Date.now() - ts) < this.GRACE_AFTER_RELOAD_MS) {
        this.graceUntil = ts + this.GRACE_AFTER_RELOAD_MS;
      }
      sessionStorage.removeItem('appVersionReloadAt');
    } catch (e) { /* ignore */ }
  }

  /** Llamado por el interceptor cada vez que ve un header X-App-Version. */
  reportServerVersion(serverVersion: string): void {
    if (!serverVersion || !this.localVersion) { return; }
    if (Date.now() < this.graceUntil) { return; } // periodo de gracia post-reload

    const match = serverVersion === this.localVersion;
    if (match) {
      // Si ya volvimos a una version compatible, descartar el snackbar abierto
      // (caso: admin revirtió el UPDATE en BD).
      if (this.snackRef) {
        this.snackRef.dismiss();
        this.snackRef = null;
      }
      return;
    }

    // Mismatch: si ya hay snackbar abierto, no abrir otro.
    if (this.snackRef) { return; }

    const cfg: MatSnackBarConfig = { duration: 0, horizontalPosition: 'right', verticalPosition: 'top', panelClass: ['snackbar-version'] };
    this.snackRef = this.snackBar.open(
      'Hay una nueva versión disponible. Por favor actualizá para evitar errores.',
      'Actualizar',
      cfg);
    this.snackRef.onAction().subscribe(() => {
      try { sessionStorage.setItem('appVersionReloadAt', Date.now().toString()); } catch (e) { /* ignore */ }
      try { (window.location as any).reload(true); } catch (e) { window.location.reload(); }
    });
    this.snackRef.afterDismissed().subscribe(() => { this.snackRef = null; });
  }
}
